import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deploymentsApi } from '../api/deployments';
import type { DeploymentPhase } from '../types';

const TERMINAL_PHASES: DeploymentPhase[] = ['completed', 'rolled_back', 'aborted', 'failed', 'preflight_failed', 'dry_run_failed'];
export const isTerminalPhase = (phase: DeploymentPhase) => TERMINAL_PHASES.includes(phase);

// Polls fast while something is actually happening (so the live log feels live without
// a websocket), and backs off when idle - still checking occasionally in case a
// colleague starts or finishes a deployment elsewhere.
export function useDeploymentSummary() {
  return useQuery({
    queryKey: ['deployments', 'current'],
    queryFn: deploymentsApi.getCurrent,
    refetchInterval: (query) => {
      const active = query.state.data?.active;
      return active && !isTerminalPhase(active.phase) ? 2_000 : 15_000;
    },
  });
}

function useDeploymentMutation<T>(fn: (arg: T) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (data) => {
      queryClient.setQueryData(['deployments', 'current'], (prev: Awaited<ReturnType<typeof deploymentsApi.getCurrent>> | undefined) =>
        prev ? { ...prev, active: data } : prev,
      );
      queryClient.invalidateQueries({ queryKey: ['deployments', 'current'] });
    },
  });
}

export function useRunPreflight() {
  return useDeploymentMutation<void>(() => deploymentsApi.runPreflight());
}

export function useRunDryRun() {
  return useDeploymentMutation<void>(() => deploymentsApi.runDryRun());
}

export function useStartPush() {
  return useDeploymentMutation<void>(() => deploymentsApi.start());
}

export function useForceAwaitingConfirmation() {
  return useDeploymentMutation<void>(() => deploymentsApi.forceAwaitingConfirmation());
}

export function useConfirmDeployment() {
  return useDeploymentMutation<{ id: string; decision: 'confirm' | 'abort' }>(({ id, decision }) =>
    deploymentsApi.confirm(id, decision),
  );
}

export function useCancelDeployment() {
  return useDeploymentMutation<{ id: string }>(({ id }) => deploymentsApi.cancel(id));
}
